<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import learningApiUser from '@src/services/api/learning/user'
import DefineValidation from '@services/validation/DefineValidation'
import Modal from '@components/modal/Modal.vue'

const router = useRouter()
const errorMessage = ref('')

const { errors, meta, handleSubmit, handleReset, models } = new DefineValidation((modules) => {
  return {
    nowPassword: modules.string().required().min(8).password().label('現在のパスワード'),
    newPassword: modules.string().required().min(8).password().label('新パスワード'),
    newPasswordConfirm: modules.string().required().oneOf([modules.ref('newPassword'), null], '入力した新しいログインパスワードと一致しません。')
  }
})

const formItems = [
  {
    id: 'nowPassword',
    type: 'password',
    label: '現在のパスワード',
    length: 32,
    placeholder: ''
  },
  {
    id: 'newPassword',
    type: 'password',
    label: '新しいパスワード',
    length: 32,
    placeholder: ''
  },
  {
    id: 'newPasswordConfirm',
    type: 'password',
    label: '新しいパスワード（確認）',
    length: 32,
    placeholder: ''
  }
]

const sendButtonDisabled = computed(() => {
  return !(meta.value.valid && meta.value.dirty)
})

const showCompleteModal = ref(false)

const toTop = () => {
  showCompleteModal.value = false
  router.push({
    name: 'Dashboard'
  })
}

const modifiable = ref(true)

const isShownNowPassword = ref(false)
const toggleNowPasswordSeek = () => {
  isShownNowPassword.value = !isShownNowPassword.value
}

const isShownNewPassword = ref(false)
const toggleNewPasswordSeek = () => {
  isShownNewPassword.value = !isShownNewPassword.value
}

const isShownNewConfirmPassword = ref(false)
const toggleConfirmPasswordSeek = () => {
  isShownNewConfirmPassword.value = !isShownNewConfirmPassword.value
}

const sendForm = handleSubmit(async (values) => {
  console.log('sendForm')
  const body = {
    password: models.newPassword.value,
    old_password: models.nowPassword.value
  }
  console.log('body', body)
  learningApiUser.editPasswrod(body).then((res) => {
    console.log('editPasswrod res', res)
    // console.log('userInfo', userInfo)
    showCompleteModal.value = true
  }).catch((err) => {
    console.error('editPasswrod err', err.response)
    if (err.response.data.error === 'oldPasswordMissmatch') {
      errorMessage.value = '現在のパスワードが違います。'
    } else if (err.response.data.error === 'samePassword') {
      errorMessage.value = '現在のパスワードと新しいパスワードが同じです。'
    } else {
      errorMessage.value = 'エラーが発生しました。'
    }
  })
})

onMounted (() => {
  // ユーザ情報取得
  learningApiUser.getUser().then((res) => {
    console.log('getUser res', res.data)
    
    // flagsにnotModifiableがあれば編集不可
    if (res.data.flags && res.data.flags.includes('notModifiable')) {
      modifiable.value = false
    }
  }).catch((err) => {
    console.error('getUser err', err.response)
  })
})
</script>

<template>
  <section>
    <div class="container">
      <h2>パスワードの変更</h2>
      <p class="topMessage">
        現在のパスワードと新しいパスワードを⼊⼒し、変更ボタンを押してください。。
        <span
          v-if="!modifiable"
          class="attention"
        >
          パスワードの変更ができないアカウントです。
        </span>
      </p>
      <form @submit.prevent="sendForm">
        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>現在のパスワード</label>
          <input
            v-model="models.nowPassword.value"
            :disabled="!modifiable"
            :type="isShownNowPassword ? 'text' : 'password'"
            class="c-input-password"
            placeholder=""
            autocomplete="off"
            maxlength="32"
          >
          <div class="c-password-btn" @click="toggleNowPasswordSeek">
            <img :src="isShownNowPassword ? require('@assets/images/icon/icon-hidden.svg') : require('@assets/images/icon/icon-display.svg')">
          </div>
          <span
            v-if="errors.nowPassword"
            class="c-validationError"
          >{{ errors.nowPassword }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>新しいパスワード</label>
          <input
            v-model="models.newPassword.value"
            :disabled="!modifiable"
            :type="isShownNewPassword ? 'text' : 'password'"
            class="c-input-password"
            placeholder=""
            autocomplete="off"
            maxlength="32"
          >
          <div class="c-password-btn" @click="toggleNewPasswordSeek">
            <img :src="isShownNewPassword ? require('@assets/images/icon/icon-hidden.svg') : require('@assets/images/icon/icon-display.svg')">
          </div>
          <span
            v-if="errors.newPassword"
            class="c-validationError"
          >{{ errors.newPassword }}</span>
        </div>

        <div class="p-form-field">
          <label
            class="c-input-label"
          ><span class="c-form-required">必須</span>新しいパスワード(確認)</label>
          <input
            v-model="models.newPasswordConfirm.value"
            :disabled="!modifiable"
            :type="isShownNewConfirmPassword ? 'text' : 'password'"
            class="c-input-password"
            placeholder=""
            autocomplete="off"
            maxlength="32"
          >
          <div class="c-password-btn" @click="toggleConfirmPasswordSeek">
            <img :src="isShownNewConfirmPassword ? require('@assets/images/icon/icon-hidden.svg') : require('@assets/images/icon/icon-display.svg')">
          </div>
          <span
            v-if="errors.newPasswordConfirm"
            class="c-validationError"
          >{{ errors.newPasswordConfirm }}</span>
        </div>

        <!-- APIエラー表示 -->
        <div
          v-if="errorMessage"
          class="p-form-error">
          {{ errorMessage }}
        </div>

        <div class="btnContainer">
          <button
            type="submit"
            :disabled="sendButtonDisabled || !modifiable"
            class="c-button c-button--medium c-button--yellow"
          >
            変更
          </button>
          <RouterLink
            :to="{name:'Dashboard'}"
            class="c-button c-button--medium c-button--border"
          >
            戻る
          </RouterLink>
        </div>
      </form>
    </div>

    <Modal :show="showCompleteModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            変更が完了しました。
          </h2>
          <div class="l-modal__buttons">
            <button
              class="c-modalBtn c-modalBtn--single c-modalBtn--gray"
              type="button"
              @click="toTop"
            >
              トップページ
            </button>
          </div>
        </div>
      </template>
    </Modal>
  </section>
</template>

<style scoped lang="scss">
.container {
  h2 {
    padding: 30px 0 15px;
    font-size: 26px;
    text-align: left;
    font-weight: bold;
  }
}
.topMessage {
  margin-bottom: 20px;
  .attention {
    display: block;
    color: #F00;
  }
}
.btnContainer {
  margin-top: 20px;
}
</style>
